import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { bal, CashBank, CashBankBook, errorText, fmtDate, fyToDate, Ledger, ledgersApi, money, Range, reportsApi } from '../accounting.api';
import BooksHeader, { RangeFields } from '../components/BooksHeader';
import { useBooksCompany } from '../components/CompanySelect';
import LedgerStatementTable from '../components/LedgerStatementTable';
import VoucherViewer from '../components/VoucherViewer';

/** Opening, in, out, closing as four tiles. */
export function SummaryTiles({ items }: { items: [string, string][] }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${items.length}, 1fr)` }, gap: 1.5 }}>
      {items.map(([k, v]) => (
        <Box key={k} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant='caption' color='text.secondary'>
            {k}
          </Typography>
          <Typography variant='h6' fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {v}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Cash Book or Bank Book: every cash (or bank) ledger's statement for the range, one after
 * another, with their combined position on top. One ledger can be picked to see just it.
 */
export default function CashBankBookPage({ kind }: { kind: CashBank }) {
  const { companies, companyId, setCompanyId, loaded } = useBooksCompany();
  const label = kind === 'CASH' ? 'Cash' : 'Bank';

  const [range, setRange] = useState<Range>(fyToDate());
  // The picked ledger belongs to the company it was picked in.
  const [picked, setPicked] = useState<{ companyId: number | null; id: number | '' }>({ companyId: null, id: '' });
  const ledgerId = picked.companyId === companyId ? picked.id : '';
  const [ledgers, setLedgers] = useState<Ledger[]>([]);
  const [book, setBook] = useState<CashBankBook | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);

  useEffect(() => {
    setLedgers([]);
    if (!companyId) return;
    ledgersApi
      .list(companyId, kind)
      .then(setLedgers)
      .catch((e) => toast.error(errorText(e, 'Could not load ledgers', 'ACCOUNTING_LEDGERS_FAILED')));
  }, [companyId, kind]);

  const load = useCallback(() => {
    if (!companyId || !range.from || !range.to) return;
    setLoading(true);
    setError(null);
    reportsApi
      .cashBank(companyId, kind, range, ledgerId === '' ? null : ledgerId)
      .then(setBook)
      .catch((e) => {
        setBook(null);
        setError(errorText(e, `Could not load the ${label.toLowerCase()} book`, `ACCOUNTING_${kind}_BOOK_FAILED`));
      })
      .finally(() => setLoading(false));
  }, [companyId, kind, range, ledgerId, label]);

  useEffect(load, [load]);

  return (
    <Stack gap={3}>
      <BooksHeader title={`${label.toUpperCase()} BOOK`} companies={companies} companyId={companyId} onCompany={setCompanyId} loaded={loaded}>
        <RangeFields value={range} onChange={setRange} />
        <TextField
          select
          size='small'
          label={`${label} ledger`}
          value={ledgerId}
          onChange={(e) => setPicked({ companyId, id: e.target.value === '' ? '' : Number(e.target.value) })}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value=''>All {label.toLowerCase()} ledgers</MenuItem>
          {ledgers.map((l) => (
            <MenuItem key={l.id} value={l.id}>
              {l.name}
            </MenuItem>
          ))}
        </TextField>
      </BooksHeader>

      {loading && <LinearProgress />}
      {error && <Alert severity='error'>{error}</Alert>}

      {book && (
        <>
          <Typography variant='body2' color='text.secondary'>
            {fmtDate(book.from)} to {fmtDate(book.to)}
            {book.ledgers.length > 1 ? ` · ${book.ledgers.length} ledgers combined` : ''}
          </Typography>
          <SummaryTiles
            items={[
              ['Opening', bal(book.opening)],
              [kind === 'CASH' ? 'Received (Dr)' : 'Deposits (Dr)', money(book.totalDebit)],
              [kind === 'CASH' ? 'Paid (Cr)' : 'Withdrawals (Cr)', money(book.totalCredit)],
              ['Closing', bal(book.closing)],
            ]}
          />
          {book.ledgers.length === 0 && <Alert severity='info'>No {label.toLowerCase()} ledgers yet.</Alert>}
          {book.ledgers.map((s) => (
            <Stack key={s.ledgerId} gap={1}>
              <Stack direction='row' justifyContent='space-between' alignItems='baseline' flexWrap='wrap' gap={1}>
                <Typography variant='subtitle1' fontWeight={700}>
                  {s.ledgerName}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {s.groupName} · Closing <b>{bal(s.closing)}</b>
                </Typography>
              </Stack>
              <LedgerStatementTable statement={s} onVoucher={setViewing} />
            </Stack>
          ))}
        </>
      )}

      <VoucherViewer companyId={companyId} voucherId={viewing} onClose={() => setViewing(null)} onChanged={load} />
    </Stack>
  );
}
